import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import type { PaginatedResponse } from '@/types/common.ts';
import type { Automation } from '@/types/automations.ts';
import type {
  SuiteSummary,
  SuiteDetail,
  SuiteBlueprintCurrent,
  SuiteBlueprintMetadata,
  SuiteBlueprintRevision,
  SuiteBlueprintProposal,
  SuiteBlueprintPreview,
  SuiteDependencyContext,
  SuiteUsage,
  SuiteMutation,
  SuiteReadiness,
} from '@/types/automationSuites.ts';
import { useSuiteIdentity } from '@/context/SuiteDraftContext/SuiteDraftContext.ts';
import { SUITE_KEYS } from '@/utils/queryKeys.ts';
import { get_automation_suite_url, get_project_suites_url } from '@/utils/urls.ts';

const suiteReadPolicy = { retry: false, staleTime: 0, refetchOnMount: 'always' as const };

function unknownReviewFreshness(versionId: string) {
  return {
    state: 'unknown' as const,
    basis: 'version_documentation' as const,
    reason_codes: ['review_freshness_unavailable'],
    limitations: ['The selected server response did not include review-freshness evidence for this exact version.'],
    source_review: null,
    target_version_id: versionId,
    scope: {
      behavior_scope: 'whole_version' as const,
      policy_resource_scope_reviewed: false,
      policy_resource_count: 0,
    },
    comparison: null,
    changed_boundaries: [],
    changed_boundary_count: 0,
    changed_boundaries_truncated: false,
  };
}

function normalizeSuiteReadiness(data: SuiteReadiness): SuiteReadiness {
  return {
    ...data,
    members: data.members.map((member) => ({
      ...member,
      review_freshness: member.review_freshness ?? {
        latest_head: member.latest_version_id ? unknownReviewFreshness(member.latest_version_id) : null,
        published_head: member.published_version_id ? unknownReviewFreshness(member.published_version_id) : null,
      },
    })),
    documentation_debt: data.documentation_debt ?? {
      status: 'unknown',
      suite_blueprint_present: false,
      membership_complete: false,
      member_findings: [],
      limitations: [
        'The selected server response did not include documentation-debt evidence; do not infer a clean result.',
      ],
    },
  };
}

export function assertSuiteIdentity(value: { suite_id?: string; id?: string }, suiteId: string) {
  if ((value.suite_id ?? value.id) !== suiteId) {
    throw new Error('Suite response belongs to a different target. Refresh before retrying.');
  }
}

export function useSuiteList(page: number, search: string, archived: boolean) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.list(orgId, projectId, identity), page, search, archived],
    enabled: !!orgId && !!projectId,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<PaginatedResponse<SuiteSummary>>(
        get_project_suites_url(projectId!),
        {
          params: { page, page_size: 50, search, archived },
          signal,
        },
      );
      if (data.results.some((suite) => suite.project_id !== projectId)) {
        throw new Error('Suite list belongs to a different project.');
      }
      return data;
    },
    ...suiteReadPolicy,
  });
}

export function useSuiteDetail(suiteId: string, page: number) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const client = useQueryClient();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.detail(orgId, projectId, suiteId, identity), page],
    enabled: !!orgId && !!suiteId,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<SuiteDetail>(get_automation_suite_url(suiteId), {
        params: { page, page_size: 50 },
        signal,
      });
      assertSuiteIdentity(data, suiteId);
      if (data.blueprint && !data.shared_text_available)
        throw new Error('Invalid protected Suite response.');
      const newer = client
        .getQueriesData<SuiteDetail>({
          queryKey: SUITE_KEYS.detail(orgId, projectId, suiteId, identity),
        })
        .some(([, previous]) => previous && previous.head_revision > data.head_revision);
      if (newer) throw new Error('Suite read predates the latest save. Refresh before continuing.');
      return data;
    },
    ...suiteReadPolicy,
  });
}

export function useSuiteBlueprint(suiteId: string, enabled: boolean) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const client = useQueryClient();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.target(orgId, projectId, suiteId, identity), 'blueprint'],
    enabled: !!suiteId && enabled,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<SuiteBlueprintCurrent>(
        `${get_automation_suite_url(suiteId)}/blueprint`,
        { signal },
      );
      assertSuiteIdentity(data, suiteId);
      if (data.revision) assertSuiteIdentity(data.revision, suiteId);
      const previous = client.getQueryData<SuiteBlueprintCurrent>([
        ...SUITE_KEYS.target(orgId, projectId, suiteId, identity),
        'blueprint',
      ]);
      if (previous && previous.head_revision > data.head_revision)
        throw new Error('Blueprint read predates the latest save. Refresh before continuing.');
      return data;
    },
    ...suiteReadPolicy,
  });
}

export function useSuiteHistory(suiteId: string, page: number, enabled: boolean) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.target(orgId, projectId, suiteId, identity), 'history', page],
    enabled,
    queryFn: async ({ signal }) =>
      (
        await api.get<PaginatedResponse<SuiteBlueprintMetadata>>(
          `${get_automation_suite_url(suiteId)}/blueprint/revisions`,
          { params: { page, page_size: 50 }, signal },
        )
      ).data,
    ...suiteReadPolicy,
  });
}

export function useSuiteRevision(suiteId: string, revisionId: string | null) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.target(orgId, projectId, suiteId, identity), 'revision', revisionId],
    enabled: !!revisionId,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<SuiteBlueprintRevision>(
        `${get_automation_suite_url(suiteId)}/blueprint/revisions/${revisionId}`,
        { signal },
      );
      assertSuiteIdentity(data, suiteId);
      if (data.id !== revisionId)
        throw new Error('Blueprint response belongs to a different revision.');
      return data;
    },
    ...suiteReadPolicy,
  });
}

export function useSuiteDependencies(suiteId: string, basis: 'latest' | 'published') {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [
      ...SUITE_KEYS.target(orgId, projectId, suiteId, identity),
      'dependencies',
      basis,
      200,
      500,
    ],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<SuiteDependencyContext>(
        `${get_automation_suite_url(suiteId)}/dependencies`,
        {
          params: { basis, node_budget: 200, edge_budget: 500 },
          signal,
        },
      );
      assertSuiteIdentity(data, suiteId);
      if (data.basis !== basis)
        throw new Error('Dependency response belongs to a different inventory basis.');
      return data;
    },
    ...suiteReadPolicy,
  });
}

export function useSuiteReadiness(suiteId: string, enabled: boolean) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.target(orgId, projectId, suiteId, identity), 'readiness', 200, 500],
    enabled: !!suiteId && enabled,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<SuiteReadiness>(`${get_automation_suite_url(suiteId)}/readiness`, {
        params: { node_budget: 200, edge_budget: 500 },
        signal,
      });
      assertSuiteIdentity(data, suiteId);
      return normalizeSuiteReadiness(data);
    },
    ...suiteReadPolicy,
  });
}

export function useSuiteUsage(suiteId: string, page: number) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.target(orgId, projectId, suiteId, identity), 'usage', page],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<SuiteUsage>(`${get_automation_suite_url(suiteId)}/usage`, {
        params: { page, page_size: 50 },
        signal,
      });
      assertSuiteIdentity(data, suiteId);
      return data;
    },
    ...suiteReadPolicy,
  });
}

export function useAutomationSuiteLinks(automationId: string, page: number) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const { orgId, projectId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.root(orgId, projectId, identity), 'anchor', automationId, page],
    enabled: !!automationId,
    queryFn: async ({ signal }) =>
      (
        await api.get<PaginatedResponse<SuiteSummary>>(
          `/automations/${automationId}/automation-suites`,
          { params: { page, page_size: 50 }, signal },
        )
      ).data,
    ...suiteReadPolicy,
  });
}

export function useSuiteMemberCandidates(projectId: string, page: number, enabled: boolean) {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const { orgId } = useOrgProject();
  return useQuery({
    queryKey: [...SUITE_KEYS.root(orgId, projectId, identity), 'member-candidates', page],
    enabled,
    queryFn: async ({ signal }) =>
      (
        await api.get<PaginatedResponse<Automation>>(`/projects/${projectId}/automations`, {
          params: { page, page_size: 50 },
          signal,
        })
      ).data,
    ...suiteReadPolicy,
  });
}

type Target = { orgId?: string; projectId: string; suiteId: string };

export function useCreateSuite() {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (variables: {
      orgId?: string;
      projectId: string;
      name: string;
      description: string;
    }) => {
      const { data } = await api.post<SuiteDetail>(get_project_suites_url(variables.projectId), {
        name: variables.name,
        description: variables.description,
      });
      if (data.project_id !== variables.projectId)
        throw new Error('Created Suite belongs to a different project.');
      return data;
    },
    onSuccess: async (_, variables) => {
      await client.invalidateQueries({
        queryKey: SUITE_KEYS.list(variables.orgId, variables.projectId, identity),
      });
    },
    retry: false,
  });
}

export function useMutateSuite() {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (variables: Target & { change: SuiteMutation }) => {
      const { suiteId, change } = variables;
      const url = get_automation_suite_url(suiteId);
      const response =
        change.kind === 'metadata'
          ? await api.patch<SuiteDetail>(url, change.body)
          : change.kind === 'archive'
            ? await api.post<SuiteDetail>(`${url}/archive`, change.body)
            : change.kind === 'member'
              ? await api.put<SuiteDetail>(`${url}/members/${change.automationId}`, change.body)
              : await api.delete<SuiteDetail>(`${url}/members/${change.automationId}`, {
                  data: change.body,
                });
      assertSuiteIdentity(response.data, suiteId);
      if (response.data.project_id !== variables.projectId)
        throw new Error('Suite mutation belongs to a different project.');
      if (response.data.head_revision < change.body.expected_head_revision)
        throw new Error('Suite mutation returned an older revision.');
      return response.data;
    },
    onSuccess: async (data, { orgId, projectId, suiteId }) => {
      const target = SUITE_KEYS.target(orgId, projectId, suiteId, identity);
      await client.cancelQueries({ queryKey: target });
      client.setQueryData(
        [...SUITE_KEYS.detail(orgId, projectId, suiteId, identity), 1],
        (previous?: SuiteDetail) =>
          previous && previous.head_revision > data.head_revision ? previous : data,
      );
      await client.invalidateQueries({ queryKey: SUITE_KEYS.root(orgId, projectId, identity) });
    },
    retry: false,
  });
}

export function usePreviewSuiteBlueprint() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async ({ suiteId, proposal }: Target & { proposal: SuiteBlueprintProposal }) => {
      const { data } = await api.post<SuiteBlueprintPreview>(
        `${get_automation_suite_url(suiteId)}/blueprint/preview`,
        proposal,
      );
      assertSuiteIdentity(data, suiteId);
      if (
        data.head_revision !== proposal.expected_head_revision ||
        data.source_revision_id !== proposal.expected_blueprint_revision_id ||
        data.body_sha256 !== proposal.expected_body_sha256 ||
        data.body_bytes !== new TextEncoder().encode(proposal.body).length
      )
        throw new Error('Blueprint preview does not match the proposal.');
      return data;
    },
    retry: false,
  });
}

export function useAppendSuiteBlueprint() {
  const api = useApiClient();
  const identity = useSuiteIdentity();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ suiteId, proposal }: Target & { proposal: SuiteBlueprintProposal }) => {
      const { data } = await api.post<SuiteBlueprintCurrent>(
        `${get_automation_suite_url(suiteId)}/blueprint/revisions`,
        proposal,
      );
      assertSuiteIdentity(data, suiteId);
      if (!data.revision) throw new Error('Blueprint save returned no revision.');
      assertSuiteIdentity(data.revision, suiteId);
      if (
        data.revision.body_sha256 !== proposal.expected_body_sha256 ||
        data.revision.body !== proposal.body ||
        data.head_revision !== proposal.expected_head_revision + 1 ||
        data.revision.id === proposal.expected_blueprint_revision_id
      )
        throw new Error('Saved blueprint does not match the proposal.');
      return data;
    },
    onSuccess: async (data, { orgId, projectId, suiteId }) => {
      const key = [...SUITE_KEYS.target(orgId, projectId, suiteId, identity), 'blueprint'];
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData(key, (previous?: SuiteBlueprintCurrent) =>
        previous && previous.head_revision > data.head_revision ? previous : data,
      );
      await client.invalidateQueries({ queryKey: SUITE_KEYS.root(orgId, projectId, identity) });
    },
    retry: false,
  });
}
